/**
 * Centralized API Service Layer
 * ─────────────────────────────────────────────────────────────────────────────
 * All database, auth, and storage interactions go through this file.
 * UI components must NEVER import from `../api/client` directly.
 * Instead, import the named functions exported here.
 *
 * Each function returns a raw `Response` so callers can inspect `.ok`, `.status`,
 * and `.json()` exactly as before — keeping migration friction minimal.
 *
 * Tenant slug is passed via X-Tenant-Slug header (not in URL paths).
 */

import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';
import type { PublicEventName } from '../config/eventCatalog';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getTenantSlug(): string | null {
  // Read from authenticated user's tenant or from URL-based resolution
  const store = useAuthStore.getState();
  return store.user?.tenant?.slug || null;
}

function tenantHeaders(slug?: string | null): Record<string, string> {
  const headers: Record<string, string> = {};
  const resolvedSlug = slug ?? getTenantSlug();
  if (resolvedSlug) {
    headers['X-Tenant-Slug'] = resolvedSlug;
  }
  return headers;
}

function authHeaders(token: string | null, slug?: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...tenantHeaders(slug) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function get(path: string, token?: string | null, slug?: string | null): Promise<Response> {
  const headers: Record<string, string> = { ...tenantHeaders(slug) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return rawFetch(path, { headers });
}

function post(path: string, body?: unknown, token?: string | null, slug?: string | null): Promise<Response> {
  return rawFetch(path, {
    method: 'POST',
    headers: authHeaders(token ?? null, slug),
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

function patch(path: string, body?: unknown, token?: string | null, slug?: string | null): Promise<Response> {
  return rawFetch(path, {
    method: 'PATCH',
    headers: authHeaders(token ?? null, slug),
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

function put(path: string, body?: unknown, token?: string | null, slug?: string | null): Promise<Response> {
  return rawFetch(path, {
    method: 'PUT',
    headers: authHeaders(token ?? null, slug),
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

function del(path: string, token?: string | null, slug?: string | null): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...tenantHeaders(slug) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return rawFetch(path, {
    method: 'DELETE',
    headers,
  });
}

// ─── Auth ───────────────────────────────────────────────────────────────────

export function requestOtp(email: string, role: string = 'artist') {
  return post('/api/public/auth/request-otp', { email, role });
}

export function verifyOtp(email: string, code: string, role: string = 'artist') {
  return post('/api/public/auth/verify-otp', { email, code, role });
}

export function getMe(token: string) {
  return post('/api/public/auth/me', undefined, token);
}

export function logout(token: string) {
  return post('/api/public/auth/logout', undefined, token);
}

export function sendVerification(email: string) {
  return post('/api/public/auth/send-verification', { email });
}

export function verifyEmailCode(email: string, code: string) {
  return post('/api/public/auth/verify-email-code', { email, code });
}

export function checkAccess(slug: string, token: string) {
  return post('/api/public/auth/check-access', { slug }, token);
}

// ─── Tenants ────────────────────────────────────────────────────────────────

export function resolveTenant(slug: string) {
  return get(`/api/public/tenants/resolve/${slug}`);
}

export function checkSlug(slug: string) {
  return get(`/api/public/tenants/check/${slug}`);
}

export function createTenant(data: {
  ownerName: string;
  ownerEmail: string;
  artistName: string;
  slug: string;
  primaryGenreId?: number;
  termsAccepted?: boolean;
}) {
  return post('/api/public/tenants', data);
}

// ─── Tenant Banner ──────────────────────────────────────────────────────────

export function getBannerConstraints() {
  return get('/api/public/banner/constraints');
}

export function presignBanner(slug: string, body: { filename: string; contentType: string; width: number; height: number; variants?: number[] }) {
  return post('/api/public/tenants/banner/presign', body, null, slug);
}

export function confirmBanner(slug: string, body: { s3Uri: string; width: number; height: number; srcset?: Record<string, string> }) {
  return post('/api/public/tenants/banner/confirm', body, null, slug);
}

// ─── Tenant Profile Image ───────────────────────────────────────────────────

export function presignProfileImage(slug: string, body: { filename: string; contentType: string; width: number; height: number }) {
  return post('/api/public/tenants/profile/presign', body, null, slug);
}

export function confirmProfileImage(slug: string, body: { s3Uri: string; width: number; height: number }) {
  return post('/api/public/tenants/profile/confirm', body, null, slug);
}

// ─── Tenant Status ──────────────────────────────────────────────────────────

export function updateTenantStatus(slug: string, statusText: string | null, token?: string | null) {
  return patch('/api/public/tenants/status', { statusText }, token, slug);
}

export function updateTenantGenres(slug: string, genreIds: number[], token?: string | null) {
  return patch('/api/public/tenants/genres', { genreIds }, token, slug);
}

// ─── Tenant Promoted Content ────────────────────────────────────────────────

export function updatePromotedContent(
  slug: string,
  body: { promotedContentType: 'vault' | 'capture' | null; promotedContentId: number | null },
  token?: string | null
) {
  return patch('/api/public/tenants/promoted-content', body, token, slug);
}

// ─── Tenant Profile Content ─────────────────────────────────────────────────

export function saveProfileContent(slug: string, content: string | null, token?: string | null) {
  return patch('/api/public/tenants/profile-content', { content }, token, slug);
}

export function publishProfileContent(slug: string, token?: string | null) {
  return post('/api/public/tenants/profile-content/publish', undefined, token, slug);
}

// ─── Tracks ─────────────────────────────────────────────────────────────────

export function listTracks(slug: string) {
  return get('/api/public/tenants/tracks', null, slug);
}

export function getTrack(slug: string, trackId: number | string) {
  return get(`/api/public/tenants/tracks/${trackId}`, null, slug);
}

export function getTrackByRef(trackReference: string) {
  return get(`/api/public/tracks/ref/${trackReference}`);
}

export function presignTrack(slug: string, body: { coverFilename: string; coverContentType: string; wavFilename: string }) {
  return post('/api/public/tenants/tracks/presign', body, null, slug);
}

export function confirmTrack(slug: string, body: { title: string; coverS3Uri: string; wavS3Uri: string; aacS3Uri?: string }) {
  return post('/api/public/tenants/tracks/confirm', body, null, slug);
}

export function updateTrack(slug: string, trackId: number | string, body: Record<string, unknown>, token?: string | null) {
  return patch(`/api/public/tenants/tracks/${trackId}`, body, token, slug);
}

export function presignTrackCover(slug: string, trackId: number | string, body: { filename: string; contentType: string }) {
  return post(`/api/public/tenants/tracks/${trackId}/cover/presign`, body, null, slug);
}

export function deleteTrack(slug: string, trackId: number | string, token?: string | null) {
  return del(`/api/public/tenants/tracks/${trackId}`, token, slug);
}

export function connectTrackExternalRelease(
  slug: string,
  trackId: number | string,
  body: { input: string },
  token?: string | null
) {
  return post(`/api/public/tenants/tracks/${trackId}/connect-release`, body, token, slug);
}

// ─── Fans ───────────────────────────────────────────────────────────────────

export function listFans(slug: string, query: string = '') {
  return get(`/api/public/tenants/fans${query}`, null, slug);
}

export function removeFan(slug: string, userId: number | string, token?: string | null) {
  return del(`/api/public/tenants/fans/${userId}`, token, slug);
}

export function importFans(slug: string, rows: { name: string; email: string }[], token?: string | null) {
  return rawFetch('/api/public/tenants/fans/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...tenantHeaders(slug), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ rows }),
  });
}

export function notifyFanRegistrationInterest(email: string) {
  return post('/api/public/fans/notify', { email });
}

// ─── Compliance Reports ─────────────────────────────────────────────────────

export function submitReport(body: { tenantId: number; email: string; reason: string; evidence?: string; pageUrl: string }) {
  return post('/api/public/reports', body);
}

export function adminListReports(params: { page?: number; status?: string }, token: string) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.status) qs.set('status', params.status);
  return get(`/api/public/admin/reports?${qs.toString()}`, token);
}

export function adminUpdateReport(id: number | string, body: { status?: string; adminNotes?: string }, token: string) {
  return patch(`/api/public/admin/reports/${id}`, body, token);
}

// ─── Compliance Audit Log ───────────────────────────────────────────────────

export function adminComplianceLogLookup(identifier: string, token: string) {
  const qs = new URLSearchParams({ identifier });
  return get(`/api/public/admin/compliance-log/lookup?${qs.toString()}`, token);
}

// ─── Memberships ────────────────────────────────────────────────────────────

export function listMemberships(slug: string) {
  return get('/api/public/tenants/memberships', null, slug);
}

export function createMembership(slug: string, body: { name: string; description?: string; fee?: number }) {
  return post('/api/public/tenants/memberships', body, null, slug);
}

export function joinMembership(membershipId: number | string, body: { userId: number }) {
  return post(`/api/public/memberships/${membershipId}/join`, body);
}

export function leaveMembership(membershipId: number | string, body: { userId: number }) {
  return post(`/api/public/memberships/${membershipId}/leave`, body);
}

export function getMembershipMembers(membershipId: number | string) {
  return get(`/api/public/memberships/${membershipId}/members`);
}

// ─── Transmissions ──────────────────────────────────────────────────────────

export function listTransmissions(slug: string, params: { page?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return get(`/api/public/tenants/transmissions${query}`, null, slug);
}

export function getTransmission(slug: string, id: number | string, token?: string | null) {
  return get(`/api/public/tenants/transmissions/${id}`, token, slug);
}

export function createTransmission(slug: string, body: Record<string, unknown>, token?: string | null) {
  return post('/api/public/tenants/transmissions', body, token, slug);
}

export function updateTransmission(slug: string, id: number | string, body: Record<string, unknown>, token?: string | null) {
  return patch(`/api/public/tenants/transmissions/${id}`, body, token, slug);
}

export function deleteTransmission(slug: string, id: number | string, token?: string | null) {
  return del(`/api/public/tenants/transmissions/${id}`, token, slug);
}

export function presignTransmissionImage(slug: string, body: { filename: string; contentType: string }, token?: string | null) {
  return post('/api/public/tenants/transmissions/content-image/presign', body, token, slug);
}

export function confirmTransmissionImage(slug: string, body: { s3Uri: string }, token?: string | null) {
  return post('/api/public/tenants/transmissions/content-image/confirm', body, token, slug);
}

// ─── Captures ───────────────────────────────────────────────────────────────

export function listCaptures(slug: string) {
  return get('/api/public/tenants/captures', null, slug);
}

export function getCapture(slug: string, captureId: number | string) {
  return get(`/api/public/tenants/captures/${captureId}`, null, slug);
}

export function createCapture(slug: string, body: Record<string, unknown>, token?: string | null) {
  return post('/api/public/tenants/captures', body, token, slug);
}

export function updateCapture(slug: string, captureId: number | string, body: Record<string, unknown>, token?: string | null) {
  return patch(`/api/public/tenants/captures/${captureId}`, body, token, slug);
}

export function deleteCapture(slug: string, captureId: number | string, token?: string | null) {
  return del(`/api/public/tenants/captures/${captureId}`, token, slug);
}

export function presignCaptureImage(slug: string, captureId: number | string, body: { filename: string; contentType: string }, token?: string | null) {
  return post(`/api/public/tenants/captures/${captureId}/image/presign`, body, token, slug);
}

export function confirmCaptureImage(slug: string, captureId: number | string, body: { s3Uri: string }, token?: string | null) {
  return post(`/api/public/tenants/captures/${captureId}/image/confirm`, body, token, slug);
}

export function resolveCaptureBySlug(slug: string | null, captureSlug: string) {
  return get(`/api/public/tenants/captures/resolve/${captureSlug}`, null, slug);
}

export function collectCapture(
  captureId: number | string,
  body: { email?: string; name?: string; sessionId?: string; visitorId?: string; shareId?: string } = {}
) {
  return post(`/api/public/captures/${captureId}/collect`, body);
}

export function createCaptureShare(
  captureId: number | string,
  body: {
    actionType: 'copy_link' | 'native_share';
    destination?: string;
    reactionId?: string;
    message?: string;
    sessionId?: string;
    visitorId?: string;
  },
  idempotencyKey?: string
) {
  return rawFetch(`/api/public/captures/${captureId}/shares`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });
}

export function getCaptureLikes(captureId: number | string, body: { visitorId?: string } = {}) {
  const qs = new URLSearchParams();
  if (body.visitorId) qs.set('visitorId', body.visitorId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return get(`/api/public/captures/${captureId}/likes${suffix}`);
}

export function likeCapture(captureId: number | string, body: { sessionId?: string; visitorId?: string; shareId?: string } = {}) {
  return post(`/api/public/captures/${captureId}/likes`, body);
}

export function unlikeCapture(captureId: number | string, body: { sessionId?: string; visitorId?: string; shareId?: string } = {}) {
  return rawFetch(`/api/public/captures/${captureId}/likes`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ─── Vaults (Pressings) ─────────────────────────────────────────────────────

export function listVaults(slug: string, params: { page?: number; limit?: number } = {}, token?: string | null) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return get(`/api/public/tenants/vaults${query}`, token, slug);
}

export function getVault(slug: string, vaultId: number | string, token?: string | null) {
  return get(`/api/public/tenants/vaults/${vaultId}`, token, slug);
}

export function createVault(slug: string, body?: Record<string, unknown>, token?: string | null) {
  return post('/api/public/tenants/vaults', body, token, slug);
}

export function updateVault(slug: string, vaultId: number | string, body: Record<string, unknown>, token?: string | null) {
  return patch(`/api/public/tenants/vaults/${vaultId}`, body, token, slug);
}

export function deleteVault(slug: string, vaultId: number | string, token?: string | null) {
  return del(`/api/public/tenants/vaults/${vaultId}`, token, slug);
}

export function addVaultTrack(slug: string, vaultId: number | string, body: { trackId: number; label?: string }, token?: string | null) {
  return post(`/api/public/tenants/vaults/${vaultId}/tracks`, body, token, slug);
}

export function removeVaultTrack(slug: string, vaultId: number | string, vaultTrackId: number | string, token?: string | null) {
  return del(`/api/public/tenants/vaults/${vaultId}/tracks/${vaultTrackId}`, token, slug);
}

export function presignVaultFile(slug: string, vaultId: number | string, body: { filename: string; contentType: string; label?: string }, token?: string | null) {
  return post(`/api/public/tenants/vaults/${vaultId}/files/presign`, body, token, slug);
}

export function confirmVaultFile(slug: string, vaultId: number | string, body: { s3Uri: string; label?: string; filename?: string }, token?: string | null) {
  return post(`/api/public/tenants/vaults/${vaultId}/files/confirm`, body, token, slug);
}

export function removeVaultFile(slug: string, vaultId: number | string, fileId: number | string, token?: string | null) {
  return del(`/api/public/tenants/vaults/${vaultId}/files/${fileId}`, token, slug);
}

export function presignVaultCover(slug: string, vaultId: number | string, body: { filename: string; contentType: string }, token?: string | null) {
  return post(`/api/public/tenants/vaults/${vaultId}/cover/presign`, body, token, slug);
}

export function confirmVaultCover(slug: string, vaultId: number | string, body: { s3Uri: string }, token?: string | null) {
  return post(`/api/public/tenants/vaults/${vaultId}/cover/confirm`, body, token, slug);
}

export function pressVault(slug: string, vaultId: number | string, body?: Record<string, unknown>, token?: string | null) {
  return post(`/api/public/tenants/vaults/${vaultId}/press`, body, token, slug);
}

export function presignVaultContentImage(slug: string, body: { filename: string; contentType: string }, token?: string | null) {
  return post('/api/public/tenants/vaults/content-image/presign', body, token, slug);
}

export function confirmVaultContentImage(slug: string, body: { s3Uri: string }, token?: string | null) {
  return post('/api/public/tenants/vaults/content-image/confirm', body, token, slug);
}

export function mintVaultKey(vaultId: number | string, body: { userId: number }) {
  return post(`/api/public/vaults/${vaultId}/mint-key`, body);
}

// ─── Event Ingestion ────────────────────────────────────────────────────────

export function recordPublicEvent(body: {
  event: PublicEventName;
  captureId?: number;
  artistSlug?: string;
  sessionId?: string;
  visitorId?: string;
  shareId?: string;
}) {
  return post('/api/public/events/record', body);
}

// ─── Fan Zone ───────────────────────────────────────────────────────────────

export function activateFanZone(userReference: string) {
  return rawFetch(`/api/public/fanzone/${userReference}/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
}

export function getFanProfile(userReference: string) {
  return rawFetch(`/api/public/fanzone/${userReference}/profile`, {
    credentials: 'include',
  });
}

export function updateFanProfile(userReference: string, body: { name: string }) {
  return rawFetch(`/api/public/fanzone/${userReference}/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
}

export function presignFanProfileImage(userReference: string, body: { filename: string; contentType: string }) {
  return rawFetch(`/api/public/fanzone/${userReference}/profile-image/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
}

export function confirmFanProfileImage(userReference: string, body: { s3Uri: string }) {
  return rawFetch(`/api/public/fanzone/${userReference}/profile-image/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
}

export function setMuteAllNotifications(userReference: string, muted: boolean) {
  return rawFetch(`/api/public/fanzone/${userReference}/mute-all`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ muted }),
    credentials: 'include',
  });
}

export function getFanPressingDetail(vaultId: number | string) {
  return rawFetch(`/api/public/fanzone/pressings/${vaultId}`, {
    credentials: 'include',
  });
}

export function getFanMusicImpact(userReference: string) {
  return rawFetch(`/api/public/fanzone/${userReference}/music-impact`, {
    credentials: 'include',
  });
}

// ─── Fan Session & OTP ──────────────────────────────────────────────────────

export function checkFanSession() {
  return rawFetch('/api/public/fanzone/session/check', {
    credentials: 'include',
  });
}

export function requestFanOtp(email: string) {
  return rawFetch('/api/public/fanzone/otp/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
    credentials: 'include',
  });
}

export function verifyFanOtp(email: string, code: string) {
  return rawFetch('/api/public/fanzone/otp/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
    credentials: 'include',
  });
}

// ─── Artist Follow ──────────────────────────────────────────────────────────

export function checkFollowingArtist(slug: string) {
  return rawFetch(`/api/public/artist/${slug}/following`, {
    credentials: 'include',
  });
}

export function followArtist(
  slug: string,
  email?: string,
  correlation?: { sessionId?: string; visitorId?: string; shareId?: string }
) {
  const payload: Record<string, unknown> = {
    ...(email ? { email } : {}),
    ...(correlation?.sessionId ? { sessionId: correlation.sessionId } : {}),
    ...(correlation?.visitorId ? { visitorId: correlation.visitorId } : {}),
    ...(correlation?.shareId ? { shareId: correlation.shareId } : {}),
  };

  return rawFetch(`/api/public/artist/${slug}/follow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include',
  });
}

export function unfollowArtist(slug: string) {
  return rawFetch(`/api/public/artist/${slug}/unfollow`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function setNotificationsMuted(slug: string, muted: boolean) {
  return rawFetch(`/api/public/artist/${slug}/notifications-muted`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ muted }),
    credentials: 'include',
  });
}

// ─── Genres ─────────────────────────────────────────────────────────────────

export interface Genre {
  id: number;
  groupId: number;
  name: string;
  slug: string;
  displayOrder: number;
}

export interface GenreGroup {
  id: number;
  name: string;
  slug: string;
  displayOrder: number;
  genres: Genre[];
}

export function listGenres(): Promise<{ data: GenreGroup[] }> {
  return get('/api/public/genres').then((res) => res.json() as Promise<{ data: GenreGroup[] }>);
}

// ─── Artist Applications ────────────────────────────────────────────────────

export function submitApplication(body: {
  artistName: string;
  email: string;
  primaryMusicLink: string;
  socialProfileUrl?: string;
  note?: string;
  sourceRef?: string;
}) {
  return post('/api/public/artist-applications', body);
}

export function getApplicationByRef(artistReference: string) {
  return get(`/api/public/artist-applications/ref/${artistReference}`);
}

// ─── Admin ──────────────────────────────────────────────────────────────────

export function adminListArtists(page: number, token: string) {
  return get(`/api/public/admin/artists?page=${page}`, token);
}

export function adminListTracks(params: { page?: number; status?: string; contentIdStatus?: string; search?: string }, token: string) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.status) qs.set('status', params.status);
  if (params.contentIdStatus) qs.set('contentIdStatus', params.contentIdStatus);
  if (params.search) qs.set('search', params.search);
  return get(`/api/public/admin/tracks?${qs.toString()}`, token);
}

export function adminListApplications(token: string) {
  return get('/api/public/admin/applications', token);
}

export function adminApproveApplication(id: number | string, token: string) {
  return post(`/api/public/admin/applications/${id}/approve`, undefined, token);
}

export function adminDeleteApplication(id: number | string, token: string) {
  return del(`/api/public/admin/applications/${id}`, token);
}

export function adminApproveTrack(trackId: number | string, token: string) {
  return post(`/api/public/admin/tracks/${trackId}/approve`, undefined, token);
}

export function adminRejectTrack(trackId: number | string, body: { reason?: string }, token: string) {
  return post(`/api/public/admin/tracks/${trackId}/reject`, body, token);
}

export function adminRequestProofOfHumanity(trackId: number | string, token: string) {
  return post(`/api/public/admin/tracks/${trackId}/proof-of-humanity`, undefined, token);
}

export function adminRetryTrack(trackId: number | string, token: string) {
  return post(`/api/public/admin/tracks/${trackId}/retry`, undefined, token);
}

export function adminOverrideTrack(trackId: number | string, token: string) {
  return post(`/api/public/admin/tracks/${trackId}/override`, undefined, token);
}

export function adminOverrideContentId(trackId: number | string, token: string) {
  return post(`/api/public/admin/tracks/${trackId}/override-content-id`, undefined, token);
}

export function adminRetryContentId(trackId: number | string, token: string) {
  return post(`/api/public/admin/tracks/${trackId}/retry-content-id`, undefined, token);
}

export function adminGetTrackAudit(trackId: number | string, token: string) {
  return get(`/api/public/admin/tracks/${trackId}/audit`, token);
}

// ─── Admin: Email Templates ─────────────────────────────────────────────────

export function adminListEmailTemplates(token: string) {
  return get('/api/public/admin/email-templates', token);
}

export function adminCreateEmailTemplate(body: { name: string }, token: string) {
  return post('/api/public/admin/email-templates', body, token);
}

export function adminUpdateEmailTemplate(id: number | string, body: Record<string, unknown>, token: string) {
  return patch(`/api/public/admin/email-templates/${id}`, body, token);
}

export function adminDeleteEmailTemplate(id: number | string, token: string) {
  return del(`/api/public/admin/email-templates/${id}`, token);
}

// ─── Admin: Email Messages ──────────────────────────────────────────────────

export function adminListEmailMessages(token: string) {
  return get('/api/public/admin/email-messages', token);
}

export function adminCreateEmailMessage(body: Record<string, unknown>, token: string) {
  return post('/api/public/admin/email-messages', body, token);
}

export function adminUpdateEmailMessage(id: number | string, body: Record<string, unknown>, token: string) {
  return patch(`/api/public/admin/email-messages/${id}`, body, token);
}

export function adminDeleteEmailMessage(id: number | string, token: string) {
  return del(`/api/public/admin/email-messages/${id}`, token);
}

// ─── Admin: Mint Queue ──────────────────────────────────────────────────────

export function adminListMintQueue(params: { page?: number; status?: string }, token: string) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.status) qs.set('status', params.status);
  return get(`/api/public/admin/mint-queue?${qs.toString()}`, token);
}

export function adminMintQueueItem(queueId: number | string, token: string) {
  return post(`/api/public/admin/mint-queue/${queueId}/mint`, undefined, token);
}

export function adminProcessMintQueue(token: string) {
  return post('/api/public/admin/process-mint-queue', undefined, token);
}

// ─── Raw fetch (escape hatch for complex upload flows) ──────────────────────

/**
 * Direct access to client.api.fetch for complex upload workflows
 * that need custom headers, XHR progress, or non-JSON bodies.
 * Use sparingly — prefer the named functions above.
 */
export function rawFetch(path: string, init?: RequestInit): Promise<Response> {
  return client.api.fetch(path, init);
}
